import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { User, Phone, Mail, LogOut, Shield, Info, ChevronRight } from 'lucide-react';

const ProfilPage = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const getRoleBadge = (role) => {
    const badges = {
      coordinatrice: { 
        bg: 'bg-gradient-to-r from-purple-500 to-purple-600', 
        icon: '👑',
        label: 'Coordinatrice' 
      },
      auxiliaire: { 
        bg: 'bg-gradient-to-r from-blue-500 to-blue-600', 
        icon: '💼',
        label: 'Auxiliaire de vie' 
      },
    };
    return badges[role] || badges.auxiliaire;
  };

  const badge = getRoleBadge(user.role);

  return (
    <div className="pb-20 bg-gradient-to-br from-blue-50 via-white to-purple-50 min-h-screen">
      {/* Header moderne */}
      <div className="bg-gradient-to-r from-blue-600 via-blue-700 to-purple-700 text-white p-6 rounded-b-[2rem] shadow-xl">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold mb-1 drop-shadow-sm">👤 Mon profil</h1>
            <p className="text-blue-100 text-sm font-medium">Informations personnelles</p>
          </div>
          <div className="bg-white/20 backdrop-blur-sm rounded-full p-3">
            <User size={28} className="text-white" />
          </div>
        </div>
      </div>

      <div className="p-4 space-y-4">
        {/* Card profil principale */}
        <div className="bg-white rounded-2xl shadow-lg border border-gray-100 overflow-hidden">
          {/* Avatar et info principale */}
          <div className="bg-gradient-to-r from-blue-50 to-purple-50 p-6">
            <div className="flex items-center gap-4">
              <div className="w-20 h-20 rounded-full bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center shadow-lg">
                <User size={40} className="text-white" />
              </div>
              <div className="flex-1">
                <h2 className="text-2xl font-bold text-gray-800 mb-1">
                  {user.prenom} {user.nom}
                </h2>
                <div className={`inline-flex items-center gap-2 px-4 py-2 rounded-xl text-white font-bold text-sm shadow-lg ${badge.bg}`}>
                  <span className="text-lg">{badge.icon}</span>
                  <span>{badge.label}</span>
                </div>
              </div>
            </div>
          </div>

          {/* Informations de contact */}
          <div className="p-6 space-y-3">
            <div className="bg-gradient-to-r from-gray-50 to-blue-50 rounded-xl p-4 border border-gray-200">
              <div className="flex items-center gap-3">
                <div className="bg-blue-500 rounded-full p-2">
                  <Mail size={20} className="text-white" />
                </div>
                <div className="flex-1">
                  <p className="text-xs text-gray-500 font-medium">Email</p>
                  <p className="font-bold text-gray-800">{user.email}</p>
                </div>
                <ChevronRight size={20} className="text-gray-400" />
              </div>
            </div>

            {user.telephone && (
              <div className="bg-gradient-to-r from-gray-50 to-green-50 rounded-xl p-4 border border-gray-200">
                <div className="flex items-center gap-3">
                  <div className="bg-green-500 rounded-full p-2">
                    <Phone size={20} className="text-white" />
                  </div>
                  <div className="flex-1">
                    <p className="text-xs text-gray-500 font-medium">Téléphone</p>
                    <p className="font-bold text-gray-800">{user.telephone}</p>
                  </div>
                  <ChevronRight size={20} className="text-gray-400" />
                </div>
              </div>
            )}

            <div className="bg-gradient-to-r from-gray-50 to-purple-50 rounded-xl p-4 border border-gray-200">
              <div className="flex items-center gap-3">
                <div className="bg-purple-500 rounded-full p-2">
                  <Shield size={20} className="text-white" />
                </div>
                <div className="flex-1">
                  <p className="text-xs text-gray-500 font-medium">Rôle</p>
                  <p className="font-bold text-gray-800 capitalize">{user.role}</p>
                </div>
                <ChevronRight size={20} className="text-gray-400" />
              </div>
            </div>
          </div>
        </div>

        {/* À propos de l'application */}
        <div className="bg-white rounded-2xl shadow-lg border border-gray-100 p-5">
          <div className="flex items-center gap-2 mb-4">
            <div className="bg-gradient-to-r from-blue-500 to-purple-500 rounded-xl p-2">
              <Info size={20} className="text-white" />
            </div>
            <h3 className="font-bold text-gray-800">À propos de l'application</h3>
          </div>
          <div className="space-y-3 text-sm">
            <div className="flex justify-between items-center py-2 border-b border-gray-100">
              <span className="text-gray-600">Version</span>
              <span className="font-bold text-gray-800">1.0.0</span>
            </div>
            <div className="flex justify-between items-center py-2 border-b border-gray-100">
              <span className="text-gray-600">Structure</span>
              <span className="font-bold text-gray-800">Planning Auxiliaires Lyon</span>
            </div>
            <div className="flex justify-between items-center py-2">
              <span className="text-gray-600">Support</span>
              <a href="mailto:contact@structure.fr" className="font-bold text-blue-600 hover:text-blue-700">
                contact@structure.fr
              </a>
            </div>
          </div>
        </div>

        {/* Bouton déconnexion */}
        <button
          onClick={handleLogout}
          className="w-full bg-gradient-to-r from-red-500 to-red-600 hover:from-red-600 hover:to-red-700 text-white font-bold py-4 rounded-2xl flex items-center justify-center gap-3 shadow-lg transition-all active:scale-95"
        >
          <LogOut size={24} />
          Se déconnecter
        </button>

        {/* Footer info */}
        <div className="bg-gradient-to-r from-blue-50 to-purple-50 rounded-2xl p-4 text-center border border-blue-100">
          <p className="text-xs text-gray-600 font-medium leading-relaxed">
            💼 Développé pour la gestion des interventions à domicile
            <br />
            <span className="text-blue-600">Planning Auxiliaires - Lyon</span>
          </p>
        </div>
      </div>
    </div>
  );
};

export default ProfilPage;
